/**
 * useTheme — Hook de gestion du mode Clair / Sombre / Système.
 *
 * Persiste la préférence dans localStorage. Applique la classe `.dark`
 * sur le <html> pour activer les variables CSS du mode sombre institutionnel
 * (ivoire sur noir profond — voir index.css).
 *
 * Modes :
 *   - "light"  : mode papier clair (défaut)
 *   - "dark"   : mode institutionnel sombre
 *   - "system" : suit la préférence OS (prefers-color-scheme)
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "elia.theme";
const VALID = ["light", "dark", "system"];


function applyTheme(mode) {
  const root = document.documentElement;
  let effective = mode;
  if (mode === "system") {
    effective = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  if (effective === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  // Meta color-scheme pour scrollbars/form-controls natifs
  root.style.colorScheme = effective;
}


function readInitial() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && VALID.includes(saved)) return saved;
  } catch { /* ignore */ }
  return "light";
}


export default function useTheme() {
  const [mode, setMode] = useState(readInitial);

  useEffect(() => {
    applyTheme(mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  // Suivre le changement OS si mode == system
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [mode]);

  const cycle = useCallback(() => {
    setMode((m) => {
      const next = { light: "dark", dark: "system", system: "light" }[m] || "light";
      return next;
    });
  }, []);

  return { mode, setMode, cycle };
}
